import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ProductionJobView } from '@models/ui/production-job-view.model';
import { formatDate } from '@angular/common';
import { SelectedRangeEvent } from '../../bates-range-select/bates-range-select.component';
import { MatDialogRef } from '@angular/material/dialog';
import { ProductionJobSdk } from '@sdks/production-job.sdk';
import { FieldLayoutViewModelService } from '@services/field-layout-view-model.service';
import { UserConfigs } from '@models/user-configs.model';
import { UserConfigsService } from '@services/user-configs.service';
import { NotificationManager } from '@managers/notification.manager';
import { UserManager } from '@managers/user.manager';
import { ProductionJob, ProductionJobStatus } from '@models/review/production-job.model';
import { combineLatest, forkJoin, of, Subject, Subscription, switchMap, take, takeUntil } from 'rxjs';
import { UserResource } from '@models/resource/user.resource';
import { ActivatedRoute } from '@angular/router';
import { NotificationHubSdk } from '@sdks/notification.hub-sdk';
import { ProductionJobsStore } from '@store/legacy/production-jobs.store';
import { cloneDeep } from "lodash-es";
import { CaseHubSdk } from "@sdks/case.hub-sdk";
import { BaseComponent } from 'src/app/components/base.component';


@Component({
    selector: 'lex-create-production-job-dialog',
    templateUrl: './create-production-job-dialog.component.html',
    styleUrls: ['./create-production-job-dialog.component.scss'],
    standalone: false
})
export class CreateProductionJobDialogComponent extends BaseComponent implements OnInit {
	public productionJobs: ProductionJobView[] = new Array<ProductionJobView>();
	public productionJobRunStatus = ProductionJobStatus;
	public users: UserResource[] = [];
	public userAlert: string;
	public selectedJob: ProductionJobView = null;
	public selectedJob$: Subject<ProductionJobView>;
	public jobTitle: string = `Production ${formatDate(new Date(), 'MM-dd-yyyy', 'en-US')}`;
	private subscription = new Subscription();
	public batesRangeGroups: Map<string, ProductionJobView[]> = new Map<string, ProductionJobView[]>();
	public userConfig: UserConfigs;

	@Output() createProductionEvent: EventEmitter<ProductionJobView> = new EventEmitter<ProductionJobView>();

	constructor(
		private userConfigService: UserConfigsService,
		private fieldLayoutViewModelService: FieldLayoutViewModelService,
		private productionJobSdk: ProductionJobSdk,
		private notificationManager: NotificationManager,
		private userManager: UserManager,
		private activatedRoute: ActivatedRoute,
		private notificationHubSdk: NotificationHubSdk,
		private caseHubSdk: CaseHubSdk,
		private productionJobsStore: ProductionJobsStore,
		private dialogRef: MatDialogRef<CreateProductionJobDialogComponent>
	) {
		super();
	}

	public ngOnInit(): void {
		this.userConfigService.getUserConfigs().subscribe((config: UserConfigs) => {
			this.userConfig = config
		});
		this.initializeProductionJobs();
		this.caseHubSdk.productionJobs$.pipe(takeUntil(this.destroy$)).subscribe(() => this.initializeProductionJobs());
	}

	public startNewPrefix(): void {
		const job = new ProductionJobView();
		job.title = `${this.jobTitle}`;
		job.batesBasePrefix = '';
		job.batesBaseNumberStart = 1;
		job.batesBaseNumberDigits = 7;
		job.isBatesStampPages = true;
		job.isCreateTiff = false;
		job.isIncludeNativeEmails = false;
		this.selectedRangeEvent({
			productionJob: job,
			lastRunProductionJob: null
		});
	}

	public selectedRangeEvent(event: SelectedRangeEvent): void {
		event.productionJob.createdById = this.userConfig.userId;
		event.productionJob.caseId = this.userConfig.currentCase.caseId;
		event.productionJob.title = this.jobTitle;
		this.productionJobSdk.createProduction(event.productionJob).pipe(
			switchMap((newProductionJob) => {
				this.productionJobsStore.setCreatedJob(newProductionJob);
				if (!event?.lastRunProductionJob) {
					this.dialogRef.close({ productionCreated: true });
					return of(null);
				}
				return forkJoin([
					this.productionJobSdk.getLoadfileFieldLayout(event.lastRunProductionJob.productionJobId),
					this.productionJobSdk.getLoadfileFieldLayout(newProductionJob.productionJobId)
				]).pipe(
					switchMap(([lastProductionFieldLayout, newProductionFieldLayout]) => {
						const clone = cloneDeep(lastProductionFieldLayout);
						clone.fieldLayoutId = newProductionFieldLayout.fieldLayoutId
						clone.fields.forEach(field => {
							field.fieldLayoutId = newProductionFieldLayout.fieldLayoutId
						})
						return this.fieldLayoutViewModelService.updateFieldLayout(clone)
					})
				)
			}),
		).subscribe(() => {
			this.dialogRef.close({ productionCreated: true });
		});
	}

	private initializeProductionJobs(): void {
		const productionJobObservable = this.productionJobSdk.get();
		const productionUsersObservable = this.userManager.getProductionJobUsers();
		combineLatest([productionJobObservable, productionUsersObservable])
			.pipe(take(1))
			.subscribe(data => {
				// data is an array of last subscriptions (next) of each of the observables above in the same order
				this.productionJobs = data[0].filter(p => p.lifecycle === 0).sort((a: ProductionJob, b: ProductionJob) => a.dateTimeCreated < b.dateTimeCreated ? 1 : -1);

				this.productionJobs.filter(p => p.status === this.productionJobRunStatus.PostRun)
					.map(p => p.productionJobId);

				this.users = data[1];

				if (this.activatedRoute.snapshot.queryParams['ProductionJobId']) {
					const job = this.productionJobs.find(j => j.productionJobId == this.activatedRoute.snapshot.queryParams['ProductionJobId']);
					if (job)
						this.selectedJob$.next(job);
				} else if (this.selectedJob)
					this.selectedJob$.next(this.productionJobs.find(j => j.productionJobId == this.selectedJob.productionJobId));
			});
	}

	public setJobTitle(newTitle: string): void {
		this.jobTitle = newTitle;
	}

	public closeDialog(): void {
		this.productionJobsStore.toggleCanceled();
		this.dialogRef.close();
	}


}
